import { DOCUMENT } from "@angular/common";
import { DestroyRef, inject, Injectable, NgZone } from "@angular/core";
import { MatDialog, MatDialogRef, MatDialogState } from "@angular/material/dialog";
import { filter, map, mergeMap, takeUntil } from "rxjs";

import {
  decideEscape,
  isDismissEscape,
  watchEscapeBaseline,
} from "@bitwarden/web-vault/app/shared/utils/escape-claim";

/**
 * The dialog's own close (X): close-button's MatDialog variant, or the one in a modal-header
 * (whose close may run custom logic via onCloseClicked).
 */
const CLOSE_BUTTON_SELECTOR =
  "[mat-dialog-close][data-close-button], modal-header [data-close-button]";

const optedOut = new WeakSet<MatDialogRef<unknown>>();

/**
 * Keeps Escape inert for a `disableClose` dialog even though it has a close (X) button, for
 * dialogs that handle Escape themselves. A plain function so callers needn't inject the service.
 * See {@link DialogEscapeService}.
 */
export function keepDialogEscapeDisabled(ref: MatDialogRef<unknown>): void {
  optedOut.add(ref);
}

/**
 * Makes Escape press the close (X) button of a `disableClose` dialog.
 *
 * `disableClose` is used across the app to stop a stray backdrop click from discarding a form,
 * but Material ties Escape to the same flag, which left keyboard users with only the X. Escape
 * now does exactly what the X does, including any custom close logic behind it. A dialog without
 * an X stays non-dismissible on purpose - e.g. a confirmation that isn't `dismissible` - so
 * Escape leaves it alone, as before.
 *
 * Something inside the dialog that used the same Escape (preventDefault() / claimEscape(), a
 * dropdown that collapsed, an appModalOverlay inside the dialog) claims it first; see
 * {@link decideEscape}. Started once by AppComponent; Material reports child injectors' dialogs
 * to the root dialog's afterOpened too.
 */
@Injectable({ providedIn: "root" })
export class DialogEscapeService {
  private readonly document = inject(DOCUMENT);

  constructor() {
    // Outside Angular: a bookkeeping listener shouldn't trigger change detection on every key.
    const stopWatching = inject(NgZone).runOutsideAngular(() =>
      watchEscapeBaseline(this.document, (event) => paneOf(event.target)),
    );

    const subscription = inject(MatDialog)
      .afterOpened.pipe(
        mergeMap((ref) =>
          ref.keydownEvents().pipe(
            filter(isDismissEscape),
            map((event) => ({ ref, event })),
            takeUntil(ref.afterClosed()),
          ),
        ),
      )
      .subscribe(({ ref, event }) => this.onEscape(ref, event));
    inject(DestroyRef).onDestroy(() => {
      subscription.unsubscribe();
      stopWatching();
    });
  }

  private onEscape(ref: MatDialogRef<unknown>, event: KeyboardEvent): void {
    // Material closes dialogs that aren't disableClose itself.
    if (!ref.disableClose || optedOut.has(ref)) {
      return;
    }
    const container = this.document.getElementById(ref.id);
    decideEscape(paneOf(container), event, (claimedInside) => {
      if (claimedInside || ref.getState() !== MatDialogState.OPEN) {
        return;
      }
      closeButtonOf(container)?.click();
    });
  }
}

function paneOf(target: EventTarget | null): HTMLElement | null {
  return target instanceof Element ? target.closest<HTMLElement>(".cdk-overlay-pane") : null;
}

/**
 * The dialog's own X - not one inside a nested modal (e.g. an appModalOverlay in the dialog), and
 * not one the layout hides (some dialogs only show their X at certain breakpoints).
 */
function closeButtonOf(container: HTMLElement | null): HTMLButtonElement | null {
  if (!container) {
    return null;
  }
  const buttons = Array.from(container.querySelectorAll<HTMLButtonElement>(CLOSE_BUTTON_SELECTOR));
  return (
    buttons.find((button) => {
      const modal = button.closest("[aria-modal='true']");
      return (
        !button.disabled &&
        button.getClientRects().length > 0 &&
        (modal === container || !container.contains(modal))
      );
    }) ?? null
  );
}
